import React, { useCallback, useEffect, useRef, useState } from 'react'

import { alpha, Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { Box as BoxIcon, RotateCcw } from 'lucide-react'

import { Skeleton } from '../../feedback/Skeleton'
/* The HOUSE tooltip, not `@mui/material`'s. Raw MUI renders the black slab this
   component used to show: `apps/app` portals its tooltips out of the themed tree,
   so the `MuiTooltip` override in `packages/ui/theme` never reached them. The
   shared component's own docblock says it outright -- "Do NOT reach past this into
   `@mui/material`" -- and this file was doing exactly that (working-conventions
   S28/S47). */
import { Tooltip } from '../../feedback/Tooltip'
import { mediaPlateSx } from '../../../theme/mediaPlate'

/**
 * VRG-749 — a product (or a captured object) as a rotatable 3D model.
 *
 * ## Why this is a shared, layer-1 component
 *
 * Two surfaces render it and they live in different apps: the consumer
 * certificate hero in `apps/app` and the product page in `apps/platform`. By
 * §47 that puts it in `@repo/ui` rather than in either app's own library, and
 * the direction of improvement is DOWN — a surface that needs something this
 * nearly does should extend it here, not fork it there.
 *
 * It is also the viewer VERA-951's `ConditionModel` should collapse onto. That
 * component is the prior art this is ported from, it still lives on the
 * unmerged `agent/VERA-951-App.ObjectCapture` branch, and it is deliberately
 * NOT edited from here (it is not reachable from this branch). The props below
 * are shaped to cover it: `surface='panel'` + `height={260}` reproduces its
 * bordered grey box exactly, so the collapse is a delete-and-reimport rather
 * than a rewrite. When it happens, VERA-951 must also DELETE its own
 * `declare global JSX` block — TypeScript merges interface members only when
 * their types are identical, and this one declares strictly more attributes, so
 * keeping both is a compile error rather than a duplicate.
 *
 * ## Why a 3D model earns its place at all
 *
 * Not as a fingerprint — that was VERA-951's original premise and it does not
 * survive the arithmetic: a scuff is 5-20 µm and the best published
 * phone-photogrammetry repeatability is ~150 µm, so any mesh-to-mesh comparison
 * sits an order of magnitude below its own noise floor.
 *
 * It earns its place as a REPRESENTATION. A certificate today shows the brand's
 * stock photography — a different unit, under studio lighting, in perfect
 * condition. This shows the product itself, from any angle the viewer wants,
 * and on a phone it can be placed in the room.
 *
 * ## `model-viewer` is a WEB COMPONENT, which matters here specifically
 *
 * `apps/app` renders inside a shadow root with its own emotion cache — the
 * reason the `@repo/ui` Alert resolves to `display: none` in there. A custom
 * element is immune to that particular trap, because its styling lives in its
 * OWN shadow root rather than in a stylesheet that has to be injected into ours.
 *
 * But it brings two of its own:
 *
 * 1. `customElements.define` is GLOBAL and throws on a second registration.
 *    Vite's HMR re-executes modules, so a plain top-level import crashes the app
 *    on the second hot update. It is therefore imported dynamically, once,
 *    behind a module-level promise.
 * 2. React 18 does not type unknown elements, hence the declaration below.
 *    React 19 handles custom elements natively; this can be deleted then.
 *
 * ## Absolute px, not `theme.spacing()`
 *
 * §47: a layer-1 component renders under whichever app's theme mounts it, and
 * the two differ — `@repo/ui`/platform is on a 4px base while `apps/app` never
 * overrode MUI's 8px default. So every `theme.spacing(n)` in here would render
 * at DOUBLE size in `apps/app`, and unlike a per-call-site `spacing={n}` there
 * is no "convert per app" available from inside the component. Hence the px
 * strings below, and no `Stack spacing` / `gap: n` anywhere.
 */

/**
 * Registration, done ONCE per page load and shared by every instance.
 *
 * Module-level rather than per-component: two models on one screen would
 * otherwise race, and the second `customElements.define` throws rather than
 * no-oping. The promise is the lock.
 */
let registration: Promise<void> | null = null

const ensureRegistered = (): Promise<void> => {
  if (!registration) {
    registration = import('@google/model-viewer')
      .then(() => undefined)
      .catch((error: Error) => {
        // Reset so a transient chunk-load failure can be retried by the next
        // mount, rather than poisoning the page for as long as it is open.
        registration = null
        throw error
      })
  }
  return registration
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        src?: string
        alt?: string
        ar?: boolean
        'ar-modes'?: string
        'camera-controls'?: boolean
        'auto-rotate'?: boolean
        'auto-rotate-delay'?: string
        'rotation-per-second'?: string
        'interaction-prompt'?: string
        'touch-action'?: string
        'shadow-intensity'?: string
        'environment-image'?: string
        'camera-orbit'?: string
        'min-camera-orbit'?: string
        'max-camera-orbit'?: string
        exposure?: string
        loading?: string
        reveal?: string
        poster?: string
      }
    }
  }
}

/**
 * One control button, shared by Reset and AR so the pair cannot drift apart.
 *
 * Absolute px rather than `theme.spacing()`: this is a layer-1 component and the two
 * apps resolve different spacing bases, so a multiplier here renders at double size in
 * `apps/app` (working-conventions §47). The surface is the card colour at 85% so the
 * button reads against both a pale plate and a dark one without a second palette.
 */
const controlButtonSx = (theme: Theme) => ({
  pointerEvents: 'auto' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  padding: 0,
  cursor: 'pointer',
  /*
   * THE HOUSE FLOATING-OVERLAY TREATMENT, copied from the certificate hero's own
   * back and share buttons (`NavigationHeader`'s `overlayControlSx`) so every
   * control floating on a product photograph reads as one family.
   *
   * It is a white PLATE at 60% with a heavy blur, not a bare glyph and not a solid
   * chip. `NavigationHeader`'s docblock carries the measurement and it is worth not
   * re-litigating: a drop-shadowed glyph with no plate computes to 1.9:1 on a light
   * hero, against WCAG's 3:1 non-text floor, where the plate holds 7.4:1 (dark) to
   * 16.5:1 (light). This surface is a MODEL on a pale backdrop, which is exactly the
   * failing case.
   *
   * NO BORDER. The house control carries a 1px 6%-black hairline, which needs
   * `!important` to survive `#vera-shadow-root *`'s ID specificity -- and at 6% on a
   * white plate it contributes essentially nothing. Omitted deliberately (user
   * directive: "no border - match those buttons styles") rather than putting an
   * `!important` into a layer-1 primitive, where it would be a trap for every other
   * consumer. The plate is self-bounding; that is the whole point of the measurement
   * above.
   *
   * `borderRadius` is the TENANT'S Studio radius, the same token the floating back
   * button and the bottom nav take, so a brand that chose square gets square chrome
   * throughout rather than a stray rounded puck.
   */
  borderRadius: `${theme.shape.borderRadius}px`,
  border: 'none',
  color: theme.palette.common.black,
  backgroundColor: alpha(theme.palette.common.white, 0.6),
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  transition: 'background-color 200ms ease',
  '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.85) },
  /* Degrade honestly where the material is unsupported: a nearly-opaque plate,
     rather than a 60%-transparent one with an illegible glyph. */
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))':
    { backgroundColor: alpha(theme.palette.common.white, 0.92) },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.brand[600]}`,
    outlineOffset: 2,
  },
})

export interface ProductModelProps {
  /** A GLB. */
  src: string
  /** The a11y label and the AR prompt. Pass the product title. */
  title?: string
  /**
   * The viewer's height. A NUMBER of px or a CSS length — it is set on the
   * container AND on the element, because without both the canvas collapses to
   * zero and renders invisibly.
   *
   * Ignored when `aspectRatio` is given; prefer that for a hero.
   */
  height?: number | string
  /**
   * Size the frame by RATIO instead of height, e.g. `'1 / 1'`.
   *
   * This is what a full-width hero wants, and the reason it exists rather than
   * the caller wrapping the component in its own square box: a percentage height
   * resolves against the PARENT's height, and this component's root is auto-height
   * (it also holds the hint line), so `height="100%"` inside a caller's
   * aspect-ratio wrapper has no definite parent to resolve against and the canvas
   * collapses to zero. Measured exactly that way on the platform product page —
   * the hint line rendered and the model did not.
   *
   * An `aspect-ratio` on the frame itself is definite with no percentage chain,
   * and it lets the hint flow BELOW the square rather than being squeezed inside
   * it.
   */
  aspectRatio?: string
  /**
   * `'bare'` (default) sits the model straight on the page with a transparent
   * background — what a hero wants, so the product reads as the page's subject
   * rather than as a thumbnail in a frame.
   *
   * `'panel'` draws the bordered, tinted box VERA-951's ConditionModel uses,
   * for a model that is one item among several on a record page.
   */
  surface?: 'bare' | 'panel'
  /** Slow idle spin. Off for a model the viewer should compose themselves. */
  autoRotate?: boolean
  /**
   * Idle spin speed. ~12deg/s by default rather than model-viewer's own 30:
   * this runs unattended behind whatever else is on the page, and at 30deg/s a
   * full turn every 12 seconds reads as a carousel demanding attention. 12
   * gives a half-minute rotation that is legible but recedes.
   */
  rotationPerSecond?: string
  /**
   * How long the spin waits before it starts, and before it RESUMES after a drag.
   *
   * 1000ms by default — model-viewer's own 3000 reads as "I broke it" after a
   * swipe. But a HOVER-revealed model wants 0: the spin is the thing the hover
   * was for, and a second of stillness first makes the reveal look like it
   * failed. So the figure belongs to the surface, not to the component.
   */
  autoRotateDelayMs?: number
  /** Hide the "drag to rotate" line where the surrounding UI already says so. */
  showHint?: boolean
  /**
   * Render the viewer's own controls — RESET, and AR where the device can do it.
   *
   * Off by default because the GRID THUMBNAIL does not want them: it resets on
   * mouse-out, so a button would be a second answer to a question the hover has
   * already answered, and it is chrome a thumbnail cannot afford.
   *
   * ⚠️ THIS PARAGRAPH USED TO NAME THE CERTIFICATE HERO AS THE OTHER SURFACE THAT
   * DID NOT WANT THEM, on the grounds that "model-viewer's own AR button is
   * already the right affordance there". The hero now passes `controls`, and the
   * old reasoning was wrong rather than merely superseded: AR is a different
   * affordance (it leaves the page for the OS viewer) and, because the button is
   * suppressed wherever `canActivateAR` is false, it is absent on every desktop --
   * so the surface the sentence described as already served had, on a laptop, no
   * reset at all. The brand panels below the hero render the control, so leaving
   * the hero without it made the page's largest model the only one that could not
   * be put back.
   *
   * If you change this default again, change this paragraph with it. A docblock
   * that outlives the behaviour it describes is worse than none: it is read as
   * current.
   *
   * ⚠️ THE AR BUTTON IS NOT RENDERED ON A DEVICE THAT CANNOT ENTER AR, which in
   * practice means it is absent on every desktop. That is deliberate: AR needs a
   * camera and an OS viewer (Scene Viewer on Android, Quick Look on iOS), and a
   * button that is present but inert is worse than no button — it reads as broken
   * rather than as unavailable. `canActivateAR` is model-viewer's own answer to
   * the question and is only meaningful after the model has loaded, so the button
   * appears with the model rather than before it.
   */
  controls?: boolean
  /**
   * VRG-780 — bump to return the camera to the pose the model loaded in.
   *
   * A signal rather than a reset BUTTON because where the reset belongs is the
   * caller's question, not this component's: a hero gets a visible control, and
   * the tag-shop thumbnail resets on mouse-out instead, so that every hover
   * starts from the same angle rather than resuming someone else's half-turn.
   * A button here would be chrome the thumbnail cannot afford.
   *
   * The pose restored is the one CAPTURED ON LOAD, not a hardcoded
   * `'0deg 75deg auto'`: model-viewer picks a framing distance per model, so a
   * literal would reset the camera to a different distance than the one the
   * model actually opened at, and only for some models.
   */
  resetSignal?: number
  /**
   * Told when the model loads or fails, so a caller can hold its own still image
   * until the canvas has something in it.
   *
   * The tag-shop thumbnail needs this: it cross-fades a static photograph to the
   * model, and without it the first hover would fade to this component's LOADING
   * SKELETON — a grey plate flashing over a perfectly good photograph, which is
   * worse than the thing it replaces. The caller keeps the still up until
   * `'ready'`.
   */
  onLoadStateChange?: (state: 'loading' | 'ready' | 'failed') => void
  sx?: SxProps<Theme>
}

export const ProductModel = ({
  src,
  title,
  height = 260,
  aspectRatio,
  surface = 'bare',
  autoRotate = true,
  rotationPerSecond = '12deg',
  autoRotateDelayMs = 1000,
  showHint = true,
  controls = false,
  resetSignal,
  onLoadStateChange,
  sx,
}: ProductModelProps) => {
  const theme = useTheme()
  const ref = useRef<HTMLElement | null>(null)
  const [registered, setRegistered] = useState(false)
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')

  /*
   * VRG-780 — `prefers-reduced-motion` suppresses the IDLE SPIN and nothing else.
   *
   * The distinction is the whole point. The auto-rotation is unattended motion in
   * the user's peripheral vision, running behind whatever they are actually
   * reading, which is exactly what the setting is for; and in a grid of tiles it
   * is motion they did not ask for on hover. DRAG is different — it is motion the
   * user is producing, frame by frame, with their own hand, and suppressing it
   * would remove the feature rather than calm it. So `camera-controls` and AR stay
   * on and only `auto-rotate` goes off.
   *
   * Read through MUI's `useMediaQuery` rather than `matchMedia` directly so it
   * re-renders when the OS setting changes mid-session instead of being sampled
   * once at mount.
   */
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const spin = autoRotate && !reduceMotion

  /* The camera pose the model opened at, captured once per `src`. */
  const homeOrbit = useRef<string | null>(null)
  /* Whether THIS device can enter AR. Never assumed — see the `controls` prop. */
  const [canAR, setCanAR] = useState(false)

  useEffect(() => {
    let cancelled = false
    ensureRegistered()
      .then(() => {
        if (!cancelled) setRegistered(true)
      })
      .catch(() => {
        if (!cancelled) setState('failed')
      })
    return () => {
      cancelled = true
    }
  }, [])

  /*
   * Two failures, not one, and this is where this viewer goes beyond the
   * VERA-951 original. That one tracked only whether the SCRIPT registered, so
   * a 404 on the GLB itself left a permanently blank canvas and no statement —
   * the exact silence its own docblock argues against. `load` / `error` on the
   * element cover the model, so a broken URL now reports.
   */
  useEffect(() => {
    const el = ref.current
    if (!registered || !el) return
    const onLoad = () => {
      /* Captured here rather than from an attribute because we never set one:
         model-viewer resolves `auto` framing from the model's own bounds, so the
         opening pose is only knowable after it has loaded. */
      const viewer = el as HTMLElement & { getCameraOrbit?: () => unknown }
      try {
        homeOrbit.current = viewer.getCameraOrbit ? String(viewer.getCameraOrbit()) : null
      } catch {
        homeOrbit.current = null
      }
      /* model-viewer resolves this from the platform's AR capability, so it is
         only meaningful once the element is live. Read here rather than polled. */
      const arCapable = el as HTMLElement & { canActivateAR?: boolean }
      setCanAR(Boolean(arCapable.canActivateAR))
      setState('ready')
    }
    const onError = () => setState('failed')
    el.addEventListener('load', onLoad)
    el.addEventListener('error', onError)
    return () => {
      el.removeEventListener('load', onLoad)
      el.removeEventListener('error', onError)
    }
  }, [registered, src])

  // Re-arm for a new src, so swapping models does not inherit the old verdict —
  // nor the previous model's framing distance, which is why the orbit clears too.
  useEffect(() => {
    setState('loading')
    homeOrbit.current = null
  }, [src])

  useEffect(() => {
    onLoadStateChange?.(state)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  /*
   * VRG-780 — return the camera to its opening pose when the caller bumps
   * `resetSignal`.
   *
   * Three calls, and each is load-bearing: `cameraOrbit` moves the goal,
   * `jumpCameraToGoal` skips the interpolation to it (so a thumbnail that has
   * already faded back to its still does not spend a second easing a camera
   * nobody can see), and `resetTurntableRotation` zeroes the rotation the IDLE
   * SPIN has accumulated, which the orbit alone does not — leave it out and a
   * tile that has been hovered for a minute reopens a minute further round.
   *
   * Skipped on the first render: `resetSignal` arrives already set, and resetting
   * to a pose that has not been captured yet would be a no-op with a failed call
   * in the console.
   */
  /*
   * The floating controls' inset, DERIVED from the surface rather than fixed at 8.
   *
   * `bare` is the full-bleed hero, where the page floats its own `AuthenticChip` at
   * 16px from the same box edge. Measured with both on screen: the chip's centre sat
   * at y=485.2 and the AR button's at 494.2 -- 9px low -- because one was inset 16
   * and the other 8 from a container bottom they SHARE (516.2px, confirmed from both
   * sides). Matching the inset puts them on one line, which is what the corner wants:
   * a status on the left, a control on the right, one band.
   *
   * `panel` keeps 8. A bordered 260px panel has its own tighter padding and no chip
   * to agree with, and 16 there would eat the model.
   */
  const controlInset = surface === 'bare' ? 16 : 8

  const resetCamera = useCallback(() => {
    const el = ref.current as
      | (HTMLElement & {
          cameraOrbit?: string
          jumpCameraToGoal?: () => void
          resetTurntableRotation?: (radians?: number) => void
        })
      | null
    if (!el || state !== 'ready') return
    try {
      if (homeOrbit.current) el.cameraOrbit = homeOrbit.current
      el.resetTurntableRotation?.(0)
      el.jumpCameraToGoal?.()
    } catch {
      /* A viewer that cannot be re-aimed is not worth failing a hover over. */
    }
  }, [state])

  const lastReset = useRef(resetSignal)
  useEffect(() => {
    if (resetSignal === lastReset.current) return
    lastReset.current = resetSignal
    resetCamera()
  }, [resetSignal, resetCamera])

  /*
   * The AR handoff. `activateAR` is model-viewer's own entry point and is the
   * only supported one — it picks Scene Viewer, Quick Look or WebXR from the
   * `ar-modes` list according to what the device actually has. Guarded on
   * `canAR` so the call is never made where it would reject.
   */
  const activateAR = useCallback(() => {
    const el = ref.current as (HTMLElement & { activateAR?: () => Promise<void> }) | null
    el?.activateAR?.().catch(() => {
      /* The OS viewer declining is the user's business, not an error state for
         the page — the model is still on screen and still turnable. */
    })
  }, [])

  /*
   * VRG-749 follow-up — the plate is the surface of the CARDS BESIDE IT.
   *
   * This read `theme.palette.grey[50]`, which in dark mode is `#18181b` while the
   * `InfoBlock` and accordions in the next column are `background.paper` (`#0f0f12`)
   * — so the viewer rendered as a lighter box that belonged to nothing. The plate
   * now reads the same token the cards do, which is also why it is a token and not
   * a hex: it cannot drift from them. See `theme/mediaPlate.ts`.
   *
   * `bare` stays transparent, unchanged. It means "the CALLER owns the surface",
   * and on the certificate page the caller's own frame paints the plate for the
   * model and the still photo alike — which is the point, since a photo and a model
   * in the same hero slot on different backgrounds is the inconsistency to avoid.
   */
  const panelSx =
    surface === 'panel' ? mediaPlateSx(theme) : { backgroundColor: 'transparent' }

  /*
   * A FAILED VIEWER SAYS SO. It does not fall back to a still image or an empty
   * box: on a provenance surface, silence about a missing 3D model is
   * indistinguishable from an item that never had one, and the two are
   * different facts about the record. The CALLER decides whether to render this
   * component at all (no `model_url` means it is never mounted); once mounted,
   * a model was promised, and a promise that cannot be kept is reported.
   */
  if (state === 'failed') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          rowGap: '8px',
          ...(aspectRatio ? { aspectRatio, width: '100%' } : { height }),
          // The same plate the loaded and loading states use, so the well does not
          // change colour on its way to reporting that it failed.
          ...mediaPlateSx(theme),
          ...sx,
        }}
      >
        <BoxIcon size={20} strokeWidth={1.5} color={theme.palette.text.secondary} />
        <Typography
          variant="body2"
          sx={{
            /*
             * `text.secondary`, which is correct again now the plate follows the
             * mode. It was briefly pinned to a constant dark ink, which was only
             * ever right for the constant light plate that got reverted — on a
             * `#0f0f12` panel a pinned dark ink would be the unreadable one.
             */
            color: theme.palette.text.secondary,
            textAlign: 'center',
            padding: '0 16px',
            lineHeight: 1.5,
          }}
        >
          The 3D view could not be loaded on this device.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={sx}>
      <Box
        sx={{
          position: 'relative',
          // Ratio wins when given — see `aspectRatio` for why a percentage height
          // cannot do this job from inside a caller's wrapper.
          ...(aspectRatio ? { aspectRatio, width: '100%' } : { height }),
          overflow: 'hidden',
          ...panelSx,
          // The element sizes to its container, and without an explicit height
          // on BOTH it collapses to zero and renders an invisible canvas.
          '& model-viewer': {
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            // The viewer owns horizontal drag; the PAGE keeps vertical scroll.
            // Belt and braces with the touch-action attribute below, which is
            // what model-viewer reads, while this covers the host box.
            touchAction: 'pan-y',
          },
          /*
           * KILL model-viewer's OWN PROGRESS BAR.
           *
           * It paints a thin dark rule across the top of the viewer while the GLB
           * downloads — which is why it showed up as "a black line on FIRST hover"
           * and never again: the second hover reads a cached model and the bar has
           * nothing to report. On the shop grid it drew straight over a product
           * photograph, with no relation to anything else on the tile.
           *
           * Removed rather than restyled because this component already answers
           * loading, and answers it better: the `state === 'loading'` Skeleton fills
           * the whole frame at the frame's own size (§19 footprint parity). Two
           * loading indicators for one load is one too many, and the one being
           * dropped is the one nobody chose.
           *
           * `::part()` is the supported seam — model-viewer exports this element as
           * `default-progress-bar` precisely so a host can take it over. Reaching
           * into its shadow DOM any other way would break on a version bump.
           */
          '& model-viewer::part(default-progress-bar)': { display: 'none' },
          /* With `controls` we render our own AR button, so model-viewer's would be
             a second one saying the same thing in a different style. */
          ...(controls ? { '& model-viewer::part(default-ar-button)': { display: 'none' } } : {}),
        }}
      >
        {registered && (
          <model-viewer
            ref={ref as React.Ref<HTMLElement>}
            src={src}
            alt={title ? `3D view of ${title}` : '3D view of this product'}
            camera-controls
            {...(spin ? { 'auto-rotate': true } : {})}
            rotation-per-second={rotationPerSecond}
            /*
             * 1s by default, against model-viewer's 3s. This is how the spin
             * RESUMES after a swipe, and three seconds of stillness after a
             * drag reads as "I broke it" rather than as a pause. A hover-revealed
             * model passes 0 — see `autoRotateDelayMs`.
             */
            auto-rotate-delay={String(autoRotateDelayMs)}
            /*
             * model-viewer's own prompt is a hand graphic that wiggles over the
             * model after a few idle seconds. Suppressed because the hint line
             * below already says it in words, and because a thing that is
             * already rotating does not need to advertise that it moves.
             */
            interaction-prompt="none"
            /*
             * The one that matters on a phone. The model occupies most of the
             * viewport on the certificate page, so with the default the page
             * cannot be scrolled past it — every vertical swipe is eaten as an
             * orbit. `pan-y` gives the page vertical and the model horizontal.
             */
            touch-action="pan-y"
            ar
            /*
             * `scene-viewer` (Android) and `quick-look` (iOS) before `webxr`.
             * The first two hand off to the OS AR viewer, which is the one the
             * phone's own camera permissions and gestures already work with;
             * webxr is the in-page fallback for a browser that has neither.
             */
            ar-modes="webxr scene-viewer quick-look"
            /*
             * A neutral studio environment rather than the default: the first
             * subject is a MATTE PLASTIC card (metalness 0), which has no
             * reflections to carry a scene and goes flat without even lighting.
             */
            environment-image="neutral"
            shadow-intensity="1"
            exposure="1"
            /* Eager: on both surfaces this IS the hero, not a thumbnail below the fold. */
            loading="eager"
          />
        )}
        {state === 'loading' && (
          /*
           * §19 footprint parity: the skeleton fills the SAME container at the
           * SAME height the loaded canvas occupies, so there is no shift when
           * it is replaced. It is an overlay rather than a sibling precisely
           * for that — the element has to be mounted to start loading and to
           * fire `load`, so the two coexist for a moment by design.
           */
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              '& > *': { width: '100%', height: '100%' },
            }}
          >
            {/* MUI's own palette-derived tint, which is right again now the plate
                follows the mode: a light bar on the light card, a lighter-than-zinc
                bar on the dark one. */}
            <Skeleton variant="rounded" width="100%" height="100%" />
          </Box>
        )}
        {controls && state === 'ready' && (
          /*
           * The viewer's controls, inside the frame rather than under it.
           *
           * Under it they would push the frame's height around — the tag shop
           * renders this inside a 1:1 gallery slide, so anything below the canvas
           * breaks the square. Inside, they are over the model's own empty corner:
           * a tag is a small object centred in its frame, so the bottom-right of
           * the plate is reliably blank.
           *
           * `pointerEvents` is restored on the buttons alone, so the rest of this
           * strip does not steal the drag it sits on top of.
           */
          /*
           * TWO corners, not one strip. RESET sits TOP-right and AR stays
           * BOTTOM-right.
           *
           * They were adjacent in one bottom-right row and that was wrong for a
           * reason beyond crowding: they are not peers. AR LEAVES the page for the
           * OS viewer and is a one-way door; reset is an undo for the gesture the
           * viewer just made. Sitting them 6px apart invited the first as a mis-tap
           * for the second, on a surface whose whole interaction is dragging with a
           * thumb -- and on a phone the bottom-right corner is exactly where that
           * thumb already rests.
           *
           * Reset goes TOP-right because it belongs to the manipulation: it is the
           * way back from a drag, so it sits away from the thumb that did the
           * dragging rather than under it. AR keeps the bottom-right corner it has
           * always had, which is also where model-viewer's own AR affordance sits
           * on surfaces that use the built-in one.
           */
          <>
            <Box
              sx={{
                position: 'absolute',
                right: controlInset,
                top: controlInset,
                display: 'flex',
                pointerEvents: 'none',
              }}
            >
              <Tooltip title="Reset the view">
                <Box
                  component="button"
                  type="button"
                  aria-label="Reset the view"
                  onClick={resetCamera}
                  sx={controlButtonSx(theme)}
                >
                  <RotateCcw size={14} strokeWidth={1.5} />
                </Box>
              </Tooltip>
            </Box>
            {canAR && (
              <Box
                sx={{
                  position: 'absolute',
                  right: controlInset,
                  bottom: controlInset,
                  display: 'flex',
                  pointerEvents: 'none',
                }}
              >
                <Tooltip title="View it in your space">
                  <Box
                    component="button"
                    type="button"
                    aria-label="View this product in your space"
                    onClick={activateAR}
                    sx={controlButtonSx(theme)}
                  >
                    {/* The CUBE, not `Scan`. A scan frame says "point this at
                      something"; the cube is the glyph model-viewer's own AR button
                      uses and the one people already read as "put this in my room".
                      User directive -- the built-in AR symbol was better. */}
                  <BoxIcon size={15} strokeWidth={1.5} />
                  </Box>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </Box>
      {showHint && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: '6px',
            marginTop: '8px',
          }}
        >
          <RotateCcw size={12} strokeWidth={1.5} color={theme.palette.text.secondary} />
          <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
            Drag to rotate. On a phone, tap the AR button to place it in the room.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
